import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type TenantStatus =
  | 'pending_payment'
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'cancelled';

export interface PlanSummary {
  id: string;
  code: string;
  name: string;
  maxVehicles: number | null;
  priceCents: number;
}

export interface SubscriptionSummary {
  id: string;
  status: string;
  billingType: string;
  cycle: string;
  valueCents: number;
  vehicleCount: number;
  nextDueDate: string | null;
  startedAt: string | null;
  externalId: string | null;
}

export interface BillingSummary {
  status: TenantStatus;
  /** Nunca pagou: cadastra a frota, mas não opera até o primeiro pagamento. */
  aguardandoPrimeiroPagamento: boolean;
  suspendedAt: string | null;
  plan: PlanSummary;
  availablePlans: PlanSummary[];
  pricePerVehicleCents: number;
  activeVehicles: number;
  projectedCents: number;
  billingDay: number;
  nextDueDate: string;
  daysUntilNextInvoice: number;
  openInvoices: number;
  provider: { name: string; environment: 'sandbox' | 'production'; fake: boolean };
  subscription: SubscriptionSummary | null;
}

export interface Invoice {
  id: string;
  status: 'pending' | 'confirmed' | 'received' | 'overdue' | 'refunded' | 'cancelled';
  description: string | null;
  valueCents: number;
  netValueCents: number | null;
  billingType: string | null;
  dueDate: string;
  paidAt: string | null;
  invoiceUrl: string | null;
  bankSlipUrl: string | null;
  pixPayload: string | null;
  createdAt: string;
}

export interface PublicPlan {
  code: string;
  name: string;
  maxVehicles: number | null;
  /** Legado do modelo por assinatura fixa. A cobrança real é por veículo. */
  priceCents: number;
  /** O que a landing mostra e o que a fatura cobra — mesma origem. */
  pricePerVehicleCents: number;
  features: Array<{ code: string; name: string }>;
}

const KEY = {
  summary: ['billing', 'summary'] as const,
  invoices: ['billing', 'invoices'] as const,
  plans: ['billing', 'plans'] as const,
};

export function useBillingSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: KEY.summary,
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const { data } = await api.get<{ data: BillingSummary }>('/billing/summary');
      return data.data;
    },
  });
}

export function useInvoices(params: { page?: number; limit?: number } = {}) {
  const { page = 1, limit = 20 } = params;
  return useQuery({
    queryKey: [...KEY.invoices, page, limit],
    queryFn: async () => {
      const { data } = await api.get<{ data: Invoice[]; meta: { total: number } }>(
        '/billing/invoices',
        { params: { page, limit } },
      );
      return data;
    },
  });
}

/** Catálogo público — não exige sessão, serve a landing e a tela de upgrade. */
export function usePublicPlans() {
  return useQuery({
    queryKey: KEY.plans,
    queryFn: async () => {
      const { data } = await api.get<{ data: PublicPlan[] }>('/billing/plans');
      return data.data;
    },
  });
}

function useBillingMutation<TInput>(
  request: (input: TInput) => Promise<BillingSummary>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      // O status do tenant muda junto — a tela de configurações lê o mesmo dado.
      void qc.invalidateQueries({ queryKey: ['billing'] });
      void qc.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
}

export function useCheckout() {
  return useBillingMutation<{ billingType: string; planCode?: string }>(async (input) => {
    const { data } = await api.post<{ data: BillingSummary }>('/billing/checkout', input);
    return data.data;
  });
}

export function useChangePlan() {
  return useBillingMutation<{ planCode: string }>(async (input) => {
    const { data } = await api.post<{ data: BillingSummary }>('/billing/change-plan', input);
    return data.data;
  });
}

export function useCancelSubscription() {
  return useBillingMutation<{ reason?: string }>(async (input) => {
    const { data } = await api.post<{ data: BillingSummary }>('/billing/cancel', input);
    return data.data;
  });
}

export function useSyncInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: { synced: number } }>('/billing/invoices/sync');
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
  });
}
